import { StudentNotificationType } from 'types/assessments'

export type Questions = {
  total?: number | null
  answered?: number | null
}

export type ActionVariant = 'view' | 'start' | 'resume'

export type PracticePayload = {
  attempt?: number | string | null
  last_activity?: string | null
  questions?: Questions | null
  time_spent?: number | null
  score?: number | string | null
  is_enabled?: boolean
  status?: string | null
  is_completed?: boolean
  is_started?: boolean
  version?: string | null
}

export type SummativePayload = {
  id?: number
  name?: string
  last_activity?: string | null
  questions?: Questions | null
  time_spent?: number | null
  score?: number | string | null
  is_started?: boolean
  is_enabled?: boolean
  is_completed?: boolean
}

export type AssessmentRow = {
  id: string
  name: string
  lastActivity: string
  answeredText: string
  progressPercentage: number
  score: string
  timeSpent: string
  isCompleted: boolean
  isEnabled: boolean
  isStarted: boolean
  actionLabel: string
  actionVariant: ActionVariant
  disabled: boolean
}

export const toNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const isAllowedNotification = (type: StudentNotificationType) => {
  const allowedNotifications = [
    StudentNotificationType.ASSESSMENTS_GRADED,
    StudentNotificationType.FORMATIVE_ASSIGNED,
    StudentNotificationType.SUMMATIVE_ASSIGNED,
    StudentNotificationType.LESSON_GUIDED_ASSIGNED,
    StudentNotificationType.INDEPENDENT_ASSIGNED,
    StudentNotificationType.CONTINUOUS_ASSIGNED
  ]

  return allowedNotifications.includes(type)
}

export const formatDate = (value?: string | null) => {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return ''

  const month = parsedDate.getMonth() + 1
  const day = parsedDate.getDate()
  const year = parsedDate.getFullYear() % 100

  return `${month}-${day}-${year.toString().padStart(2, '0')}`
}

export const formatLastActivity = (value?: string | null) => {
  const formattedDate = formatDate(value)
  return formattedDate
}

export const formatScore = (score?: number | string | null) => {
  if (score === null || score === undefined || score === '') return ''
  if (typeof score === 'number') return `${Math.round(score)}%`
  return score.includes('%') ? score : `${score}%`
}

export const formatMinutesToHoursAndMinutes = (value?: number | null) => {
  if (value === null || value === undefined) return ''

  const totalMinutes = Math.max(0, Math.floor(toNumber(value)))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours > 0 && minutes > 0) return `${hours}h, ${minutes}m`
  if (hours > 0) return `${hours}h`
  return `${minutes}m`
}

const resolveFormativeAction = (
  payload: PracticePayload | undefined,
  isQuiz: boolean = false
) => {
  const isEnabled = Boolean(payload?.is_enabled)
  const isCompleted = Boolean(payload?.is_completed)
  const isStarted = Boolean(payload?.is_started)

  if (!isEnabled && isCompleted) {
    return {
      actionLabel: 'View',
      resolvedActionVariant: 'view' as ActionVariant,
      disabled: false
    }
  }

  if (isCompleted) {
    return {
      actionLabel: 'View',
      resolvedActionVariant: 'view' as ActionVariant,
      // we allow the student to view formative quiz after complete
      disabled: isQuiz ? false : !isEnabled
    }
  }

  if (isStarted) {
    return {
      actionLabel: 'Resume',
      resolvedActionVariant: 'resume' as ActionVariant,
      disabled: !isEnabled
    }
  }

  if (isEnabled && isCompleted) {
    return {
      actionLabel: 'View',
      resolvedActionVariant: 'view' as ActionVariant,
      disabled: isQuiz ? false : !isEnabled
    }
  }

  return {
    actionLabel: 'Start',
    resolvedActionVariant: 'start' as ActionVariant,
    disabled: !isEnabled
  }
}

export const mapFormativeRow = (rowName: any, payload: any) => {
  const isQuiz = payload?.isAssessment
  const questions = payload?.questions
  const total = toNumber(questions?.total)
  const answered = toNumber(questions?.answered)
  const progressPercentage =
    total > 0 ? Math.round((Math.min(answered, total) / total) * 100) : 0
  const { actionLabel, resolvedActionVariant, disabled } =
    resolveFormativeAction(payload, isQuiz)

  return {
    id: payload?.id,
    name: rowName,
    lastActivity: formatLastActivity(payload?.last_activity),
    answeredText: total > 0 ? `${answered} / ${total}` : '',
    progressPercentage,
    score: formatScore(payload?.score),
    timeSpent: formatMinutesToHoursAndMinutes(payload?.time_spent),
    actionLabel,
    actionVariant: resolvedActionVariant,
    disabled,
    isStarted: Boolean(payload?.is_started),
    isCompleted: Boolean(payload?.is_completed),
    isQuiz,
    /**
     * Preserve the backend-provided formative attempt and fall back to attempt
     * one when the endpoint does not provide a value.
     */
    attempt: payload?.attempt || 1,
    quizVersion: payload?.version || 'A'
  }
}

export const mapSummativeRow = (
  item: SummativePayload,
  index: number
): AssessmentRow => {
  const isBenchA2 = item.name?.toLowerCase().includes('a2')
  const questions = item.questions
  const total = toNumber(questions?.total)
  const answered = toNumber(questions?.answered)
  const progressPercentage =
    total > 0 ? Math.round((Math.min(answered, total) / total) * 100) : 0
  const isEnabled = Boolean(item.is_enabled)
  const isCompleted = Boolean(item.is_completed)
  const isStarted = Boolean(item.is_started)

  let actionLabel = 'Start'
  let actionVariant: ActionVariant = 'start'
  let disabled = !isEnabled

  if (isCompleted) {
    actionLabel = 'View'
    actionVariant = 'view'
    // Only Benchmark A2 is viewable by student after it has been submitted
    disabled = isBenchA2 ? false : !isEnabled

    return {
      id: String(item.id || `summative-row-${index + 1}`),
      name: item.name || `Summative ${index + 1}`,
      lastActivity: formatLastActivity(item.last_activity),
      answeredText: total > 0 ? `${answered} / ${total}` : '',
      progressPercentage,
      score: formatScore(item.score),
      timeSpent: formatMinutesToHoursAndMinutes(item.time_spent),
      actionLabel,
      isCompleted,
      isEnabled,
      isStarted,
      actionVariant,
      disabled
    }
  }

  // we allow the students to resume summative if it has been started but not completed yet, given that the summative is enabled
  if (isStarted) {
    actionLabel = 'Resume'
    actionVariant = 'resume'
    disabled = !isEnabled

    return {
      id: String(item.id || `summative-row-${index + 1}`),
      name: item.name || `Summative ${index + 1}`,
      lastActivity: formatLastActivity(item.last_activity),
      answeredText: total > 0 ? `${answered} / ${total}` : '',
      progressPercentage,
      score: formatScore(item.score),
      timeSpent: formatMinutesToHoursAndMinutes(item.time_spent),
      actionLabel,
      isCompleted,
      isEnabled,
      isStarted,
      actionVariant,
      disabled
    }
  }

  return {
    id: String(item.id || `summative-row-${index + 1}`),
    name: item.name || `Summative ${index + 1}`,
    lastActivity: formatLastActivity(item.last_activity),
    answeredText: total > 0 ? `${answered} / ${total}` : '',
    progressPercentage,
    score: formatScore(item.score),
    timeSpent: formatMinutesToHoursAndMinutes(item.time_spent),
    actionLabel,
    isCompleted,
    isEnabled,
    isStarted,
    actionVariant,
    disabled
  }
}
